import type {
  TFooterNavigationLink,
  TFooterNavigationSection,
} from "@/ui/components/navigation/Footer";
import {
  isGermanLocale,
  type TSupportedLocale,
} from "libs/i18n/src/lib/locales";

import {
  getBasePrimarySections,
  getBaseSecondaryLinks,
  getGermanArticleLinks,
  getGermanHelpLinks,
  getSocialLinks,
  GERMAN_IMPRESSUM_HREF,
  LINK_IDS,
  OMITTED_GERMAN_PRIMARY_LINK_IDS,
  SECTION_IDS,
  SIGNED_IN_HREF_BY_LINK_ID,
  type TFooterNavigationTranslator,
} from "./footer-navigation.config";

// ---------------------------------------------------------------------------
// Locale transforms
// ---------------------------------------------------------------------------

const applyGermanPrimaryNavigation = (
  sections: TFooterNavigationSection[],
  t: TFooterNavigationTranslator,
): TFooterNavigationSection[] => sections.map((section) => {
  if (section.id === SECTION_IDS.articles) {
    return { ...section, links: getGermanArticleLinks(t) };
  }
  const links = section.links.filter(
    (link) => !OMITTED_GERMAN_PRIMARY_LINK_IDS.has(link.id),
  );
  if (section.id !== SECTION_IDS.help) {
    return { ...section, links };
  }
  return {
    ...section,
    links: [ ...links, ...getGermanHelpLinks(t) ],
  };
});

const applyGermanSecondaryNavigation = (
  links: TFooterNavigationLink[],
  t: TFooterNavigationTranslator,
): TFooterNavigationLink[] => {
  const withoutSitemap = links.filter(
    (link) => link.id !== LINK_IDS.sitemap,
  );
  const impressum: TFooterNavigationLink = {
    id: "impressum",
    label: t("Secondary.impressum"),
    href: GERMAN_IMPRESSUM_HREF,
  };
  const cookieSettingsIndex = withoutSitemap.findIndex(
    (link) => link.id === LINK_IDS.cookieSettings,
  );
  if (cookieSettingsIndex === -1) {
    return [ ...withoutSitemap, impressum ];
  }
  return [
    ...withoutSitemap.slice(0, cookieSettingsIndex),
    impressum,
    ...withoutSitemap.slice(cookieSettingsIndex),
  ];
};

// ---------------------------------------------------------------------------
// Auth transforms
// ---------------------------------------------------------------------------

const withSignedInHrefs = (
  sections: TFooterNavigationSection[],
): TFooterNavigationSection[] => sections.map((section) => ({
  ...section,
  links: section.links.map((link) => {
    const href = SIGNED_IN_HREF_BY_LINK_ID[link.id];
    if (!href) {
      return link;
    }
    return { ...link, href };
  }),
}));

// ---------------------------------------------------------------------------
// Navigation builders
// ---------------------------------------------------------------------------

export function getFooterPrimaryNavigation(
  t: TFooterNavigationTranslator,
  locale: TSupportedLocale,
): TFooterNavigationSection[] {
  const sections = getBasePrimarySections(t);
  if (!isGermanLocale(locale)) {
    return sections;
  }
  return applyGermanPrimaryNavigation(sections, t);
}

export function getFooterSignedInPrimaryNavigation(
  t: TFooterNavigationTranslator,
  locale: TSupportedLocale,
): TFooterNavigationSection[] {
  return withSignedInHrefs(getFooterPrimaryNavigation(t, locale));
}

export function getFooterSecondaryNavigation(
  t: TFooterNavigationTranslator,
  locale: TSupportedLocale,
): TFooterNavigationLink[] {
  const links = getBaseSecondaryLinks(t);
  if (!isGermanLocale(locale)) {
    return links;
  }
  return applyGermanSecondaryNavigation(links, t);
}

export function getFooterSocialNavigation(
  t: TFooterNavigationTranslator,
): TFooterNavigationLink[] {
  return getSocialLinks(t);
}
